import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, CheckCircle, ClipboardList } from 'lucide-react';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import { assessmentService } from '@services';
import {
  canUserReassignAssessment,
  canUserEvaluateItems,
  canUserGenerateAssessmentItems
} from '@utils/permissionUtils';
import { useAssessmentChat } from '@hooks/useAssessmentChat';
import PageLayout from '@components/layout/PageLayout';
import Button from '@components/common/Button';
import DetailPageHeader from '@components/common/DetailPageHeader';
import ConfirmDialog from '@components/common/ConfirmDialog';
import Badge from '@components/common/Badge';
import EntityActivity from '@components/common/EntityActivity';
// Temporarily hidden
// import { FloatingChat } from '@components/features/chat/FloatingChat';
import {
  AssessmentStatusOverview,
  AssessmentDomains,
  AssessmentFrameworkManager,
  AssessmentAssignment,
  AssessmentReportSection,
  CancelAssessmentAction,
  CancellationBanner
} from '@components/features/assessments';
import { AttachmentSection } from '@components/features/attachments';
import AssessmentSubmissionSummary from '@components/features/assessments/AssessmentSubmissionSummary';
import EvidenceRequirementsSummary from '@components/features/assessments/EvidenceRequirementsSummary';
import { ApiError, ROLES, AssessmentStatus } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { statusToVariant } from '@utils/badgeMappers';
import { formatAssessmentId } from '@utils/exportUtils';
import { getApiErrorStatus } from '@utils/apiError';
import { calculateAssessmentProgress } from '@utils/assessmentProgress';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import styles from './AssessmentDetailPage.module.css';

const EVIDENCE_PHASE_STATUSES: AssessmentStatus[] = ['Pending Evidence', 'Evidence Past Due', 'Escalated'];

const AssessmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const [showAssignmentModal, setShowAssignmentModal] = React.useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);

  // Ensure chat room exists for this assessment
  useAssessmentChat(id);

  // Fetch assessment details
  const {
    data: assessment,
    isLoading,
    error
  } = useQuery({
    queryKey: ['assessment', id],
    queryFn: () => assessmentService.getAssessment(Number(id)),
    enabled: !!id
  });

  // Fetch assessment items to calculate progress
  const { data: assessmentItems } = useQuery({
    queryKey: ['assessment-items', id],
    queryFn: () => assessmentService.getAssessmentItems(Number(id)),
    enabled: !!id
  });

  const scrollToItemId = (location.state as { scrollToItemId?: number } | null)?.scrollToItemId;

  React.useLayoutEffect(() => {
    if (!scrollToItemId || !assessmentItems?.items?.length) {
      return;
    }
    document.getElementById(`process-item-${scrollToItemId}`)?.scrollIntoView({ block: 'center' });
  }, [scrollToItemId, assessmentItems?.items]);

  // Refresh function for when assessment items are updated (handled by the modal)
  const refreshAssessmentData = () => {
    // Refetch assessment items and assessment details to update progress
    queryClient.invalidateQueries({ queryKey: ['assessment-items', id] });
    queryClient.invalidateQueries({ queryKey: ['assessment', id] });
  };

  // Mutation for generating assessment items
  const generateItemsMutation = useMutation({
    mutationFn: (assessmentId: number) => assessmentService.generateAssessmentItems(assessmentId),
    onSuccess: () => {
      // Refetch assessment items after generation
      queryClient.invalidateQueries({ queryKey: ['assessment-items', id] });
    }
  });

  // Mutation for completing assessment
  const completeAssessmentMutation = useMutation({
    mutationFn: (assessmentId: number) => assessmentService.completeAssessment(assessmentId),
    onSuccess: async (data) => {
      // Refetch assessment to update status
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['assessment', id] }),
        queryClient.invalidateQueries({ queryKey: ['assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-table'] }),
        queryClient.invalidateQueries({ queryKey: ['assessments-counts'] }),
        queryClient.invalidateQueries({ queryKey: ['supplier-assessments'] }),
        queryClient.invalidateQueries({ queryKey: ['contract-assessments'] })
      ]);

      // Explicitly refetch key queries
      queryClient.refetchQueries({ queryKey: ['assessments-counts'] });

      // Show success modal
      setShowSuccessModal(true);

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 5000);
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.error || apiError.message || 'Failed to publish assessment';

      // If there are items missing evidence, show detailed message
      const responseData = apiError.response?.data as {
        items_missing_evidence?: Array<{ process_name: string; evaluation: string; evidence_status: string }>;
      };
      const itemsMissingEvidence = responseData?.items_missing_evidence;
      if (itemsMissingEvidence && Array.isArray(itemsMissingEvidence)) {
        const itemsList = itemsMissingEvidence
          .map((item) => `• ${item.process_name} (${item.evaluation}) - Evidence: ${item.evidence_status}`)
          .join('\n');
        alert(`${errorMessage}\n\nItems requiring approved evidence:\n${itemsList}`);
      } else {
        alert(errorMessage);
      }
    }
  });

  const handleBack = () => {
    navigate(-1); // Go back to previous page in browser history
  };

  const [isGeneratingReport, setIsGeneratingReport] = React.useState(false);
  const [reportError, setReportError] = React.useState<string | null>(null);

  const handleGenerateReport = async () => {
    if (!id) return;

    setIsGeneratingReport(true);
    setReportError(null);

    try {
      await assessmentService.generateAssessmentReport(Number(id));
      queryClient.invalidateQueries({ queryKey: ['assessment-reports', Number(id)] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate report';
      setReportError(errorMessage);
      console.error('Error generating report:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleAssign = () => {
    setShowAssignmentModal(true);
  };

  const handleUpdateItem = () => {
    refreshAssessmentData();
  };

  const handleGenerateFramework = () => {
    if (id) {
      generateItemsMutation.mutate(Number(id));
    }
  };

  const handleCompleteAssessment = () => {
    setShowSubmitConfirm(true);
  };

  const handleConfirmSubmit = () => {
    if (id) {
      completeAssessmentMutation.mutate(Number(id));
      setShowSubmitConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <LoadingState message="Loading assessment details..." spinnerSize="large" />
      </PageLayout>
    );
  }

  if (error) {
    const isForbidden = getApiErrorStatus(error) === 403;
    return (
      <PageLayout>
        <ErrorState
          title={isForbidden ? 'Access Denied' : 'Assessment Not Found'}
          message={
            isForbidden
              ? 'You do not have permission to view this assessment.'
              : "The assessment you're looking for could not be found."
          }
          action={
            <Button variant="primary" onClick={handleBack}>
              <ArrowLeft size={16} />
              Go Back
            </Button>
          }
        />
      </PageLayout>
    );
  }

  if (!assessment) {
    return null;
  }

  // Security is handled by the backend - if we can fetch the assessment, we have permission to view it

  const canReassign =
    permissions.canReassignAssessments &&
    canUserReassignAssessment(user!, assessment) &&
    assessment.assessment_status !== 'Final';
  const canEvaluate = canUserEvaluateItems(user!, assessment);
  const canGenerate = canUserGenerateAssessmentItems(user!, assessment);

  const progress = calculateAssessmentProgress(assessmentItems?.items, assessmentItems?.total_items ?? 0);

  const backLabel = 'Go Back'; // Generic back label - goes to previous page

  const renderSupplierMetadata = (): React.ReactNode => {
    if (!assessment.supplier_name) return undefined;
    if (!permissions.canAccessSuppliers) return <span>{assessment.supplier_name}</span>;
    return (
      <button
        type="button"
        className={styles.supplierLink}
        onClick={() => navigate(`/suppliers/${assessment.supplier_id}`)}
      >
        {assessment.supplier_name}
      </button>
    );
  };

  return (
    <PageLayout>
      <DetailPageHeader
        icon={<ClipboardList size={24} />}
        title={`Assessment ${formatAssessmentId(assessment.assessment_id)}`}
        subtitle={assessment.assessment_name}
        metadata={renderSupplierMetadata()}
        backLabel={backLabel}
        onBack={handleBack}
        actions={
          <>
            <CancelAssessmentAction assessment={assessment} />
            <Button variant="secondary" onClick={handleGenerateReport} disabled={isGeneratingReport}>
              <FileText size={16} />
              {isGeneratingReport ? 'Generating...' : 'Generate Report'}
            </Button>
          </>
        }
      />

      <CancellationBanner assessment={assessment} />

      {/* Report Generation Error */}
      {reportError && (
        <div className="alert alert-error" style={{ margin: '16px 0' }}>
          <span>{reportError}</span>
          <button className="alert-close" onClick={() => setReportError(null)} aria-label="Close">
            ×
          </button>
        </div>
      )}

      <AssessmentStatusOverview
        assessment={assessment}
        progress={progress}
        canReassign={canReassign}
        onAssign={handleAssign}
        onComplete={handleCompleteAssessment}
        canComplete={canEvaluate}
      />

      {/* Evidence Requirements Summary - Show during evidence collection phase */}
      {EVIDENCE_PHASE_STATUSES.includes(assessment.assessment_status) &&
        assessmentItems?.items && (
          <div data-test-id="evidence-requirements-summary">
            <EvidenceRequirementsSummary assessmentItems={assessmentItems.items} assessment={assessment} />
          </div>
        )}

      <div className={styles.detailContent}>
        <div className={styles.detailMain}>
          {/* Framework Tools - Hidden for Admin users who focus on high-level management */}
          {!hasRole(user, ROLES.ADMIN) && (
            <AssessmentFrameworkManager
              assessment={assessment}
              assessmentItems={assessmentItems}
              canGenerate={canGenerate}
              onGenerate={handleGenerateFramework}
              isGenerating={generateItemsMutation.isPending}
            >
              <AssessmentDomains
                assessmentItems={assessmentItems?.items || []}
                canUpdate={canEvaluate}
                onUpdateItem={handleUpdateItem}
                contractNumber={assessment.contract_number}
                supplierName={assessment.supplier_name}
                assessmentStatus={assessment.assessment_status}
                assessorId={assessment.assessor_id}
              />
            </AssessmentFrameworkManager>
          )}

          <AttachmentSection
            entityType="assessment"
            entityId={assessment.assessment_id}
            canManage={permissions.isAdmin || permissions.isAssessor}
          />

          <AssessmentReportSection assessmentId={assessment.assessment_id} />

          {permissions.canAccessNotes && (
            <EntityActivity entityType="assessment" entityId={assessment.assessment_id} title="Assessment Activity" />
          )}
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <AssessmentAssignment
          assessment={assessment}
          onClose={() => setShowAssignmentModal(false)}
          onAssigned={() => {
            // Refresh the assessment data
            queryClient.invalidateQueries({ queryKey: ['assessment', id] });
          }}
        />
      )}

      {/* Confirmation Dialog for Assessment Submission */}
      {showSubmitConfirm && assessmentItems?.items && (
        <ConfirmDialog
          isOpen={showSubmitConfirm}
          title="Publish Assessment?"
          message=""
          confirmText="Publish Assessment"
          cancelText="Cancel"
          confirmVariant="primary"
          onConfirm={handleConfirmSubmit}
          onCancel={() => setShowSubmitConfirm(false)}
          dataTestId="submit-assessment-dialog"
        >
          <div>
            <AssessmentSubmissionSummary progress={progress} assessmentItems={assessmentItems.items} />
            <p className="text-muted text-base mt-4 leading-relaxed">
              This will generate the initial assessment report and notify Contract Manager(s) and Owner(s). Continue?
            </p>
          </div>
        </ConfirmDialog>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={styles.successModalOverlay} data-test-id="success-modal-overlay">
          <button
            type="button"
            className={styles.successModalOverlayBackdrop}
            onClick={() => setShowSuccessModal(false)}
            tabIndex={-1}
            aria-hidden="true"
          />
          <div className={styles.successModal} data-test-id="success-modal">
            <div className={styles.successIcon}>
              <CheckCircle size={48} />
            </div>
            <h3>Assessment Published Successfully!</h3>
            <div className={styles.successDetails}>
              <p>✅ Initial report has been generated</p>
              <p>📧 Contract Manager(s) and Owner(s) have been notified</p>
              <p>
                📋 Status updated to:{' '}
                <Badge variant={statusToVariant(assessment.assessment_status)}>{assessment.assessment_status}</Badge>
              </p>
              {EVIDENCE_PHASE_STATUSES.includes(assessment.assessment_status) && (
                <p>⏰ Pending evidence phase has begun</p>
              )}
            </div>
            <Button onClick={() => setShowSuccessModal(false)}>Got it</Button>
          </div>
        </div>
      )}

      {/* Assessment-specific floating chat */}
      {/* Temporarily hidden */}
      {/* <FloatingChat currentAssessmentId={assessment?.assessment_id} /> */}
    </PageLayout>
  );
};

export default AssessmentDetailPage;
